import { InternationalMdnInput, removeMdnFormatting } from "@expert/common-ui";
import { Stack, Text } from "@mantine/core";
import type { CountryCode } from "libphonenumber-js";
import { useEffect, useMemo } from "react";
import { MdnInput } from "@/common-ui";
import type { Task } from "@/sdk";
import { useCallbacksRemaining, useSession, useSessionActiveCallerId } from "@/sdk";
import { formatPhoneNumber, useAsyncLoading, validatePhoneNumber } from "@/shared-utils";
import { useCallbacksFormStore } from "../../callbacksForm.store";
import type { CallbackFormVariant } from "../../types";
import { useScheduleCallback } from "../../useScheduleCallback";
import { CallbackActions } from "./CallbackActions";
import { CallbackTypeSelection } from "./CallbackTypeSelection";

// TODO: remove onBackClick & afterSubmitClickDefault & reachedCallbackCutoff when DeprecatedSessionSummary is deleted.
interface ScheduleCallbackFormProps {
    onBackClick?: () => void;
    afterSubmitClickDefault?: () => void;
    shouldShowActions?: boolean;
    reachedCallbackCutoff?: boolean;
    isInternationalPhoneEnabled?: boolean;
    defaultCountry?: CountryCode;
}

function getFormVariant(activeTask: Task | undefined) {
    if (!activeTask) {
        return "post-call";
    }

    return activeTask.status === "wrapping" ? "post-call" : "active-call";
}

export function ScheduleCallbackForm({
    onBackClick,
    afterSubmitClickDefault,
    shouldShowActions = true,
    reachedCallbackCutoff,
    isInternationalPhoneEnabled,
    defaultCountry,
}: ScheduleCallbackFormProps) {
    const {
        selectedCallbackMdn,
        callbackScheduled,
        setCallbackType,
        setSelectedCallbackMdn,
        setSelectedCallbackNowDelay,
        selectedCallbackOption,
        resetState,
    } = useCallbacksFormStore();

    const session = useSession();

    const activeTask = session.currentTask;
    const { scheduleCallback } = useScheduleCallback();
    const activeSessionMdn = useSessionActiveCallerId();

    const { callbacksRemaining } = useCallbacksRemaining();
    const defaultMdn = useMemo(
        () =>
            callbackScheduled && selectedCallbackMdn ? formatPhoneNumber(selectedCallbackMdn).number : activeSessionMdn,
        // oxlint-disable-next-line exhaustive-deps
        [activeSessionMdn],
    ); //? calculates once only when panel is mounted or activeSessionMdn changes

    const variant: CallbackFormVariant = getFormVariant(activeTask);
    const setDefaultValues = () => {
        switch (variant) {
            case "active-call":
                setCallbackType("callback-user-with-delay");
                break;
            case "post-call":
                setCallbackType("callback-user-now");
                setSelectedCallbackNowDelay(0);
                break;
        }
    };

    useEffect(() => {
        if (!callbackScheduled) {
            resetState();
            setDefaultValues();
            if (activeSessionMdn) setSelectedCallbackMdn(activeSessionMdn);
        } else if ((!selectedCallbackMdn || !validatePhoneNumber(selectedCallbackMdn).valid) && activeSessionMdn) {
            setSelectedCallbackMdn(activeSessionMdn);
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [activeSessionMdn]); //? runs only when panel is mounted or activeSessionMdn changes

    const [isSubmitting, onSubmit] = useAsyncLoading(async () => {
        await scheduleCallback();
        // running after submit actions if any were passed in via props
        if (afterSubmitClickDefault) afterSubmitClickDefault();
    });

    // Calculate phone validation
    const isCallbackMdnValid = selectedCallbackMdn
        ? validatePhoneNumber(selectedCallbackMdn, defaultCountry).valid
        : false;

    const props = {
        value: selectedCallbackMdn,
        onInput: (event: React.ChangeEvent<HTMLInputElement>) => {
            const normalizedMdn = removeMdnFormatting(event.currentTarget.value);
            setSelectedCallbackMdn(normalizedMdn);
        },
        autoFocus: true,
        maw: "30rem",
        mb: "xs",
        size: "sm",
        defaultCountry,
        defaultValue: defaultMdn,
    };

    return (
        <Stack>
            <Stack gap="xs" mb="xl">
                {callbacksRemaining <= 1 && (
                    <Text size="xs" c={"warning.5"}>
                        {callbacksRemaining} callback remaining
                    </Text>
                )}
                <Text size="xs">Callback number</Text>
                {isInternationalPhoneEnabled ? <InternationalMdnInput {...props} /> : <MdnInput {...props} />}
                <Text size="xs">Callback time</Text>
                <CallbackTypeSelection value={selectedCallbackOption} variant={variant} />
            </Stack>
            {shouldShowActions && (
                <CallbackActions
                    onSubmit={onSubmit}
                    onBackClick={onBackClick}
                    formVariant={variant}
                    isSubmitting={isSubmitting}
                    reachedCallbackCutoff={reachedCallbackCutoff}
                    isCallbackMdnValid={isCallbackMdnValid}
                    data-testid="call-controls-callback-schedule-complete"
                />
            )}
        </Stack>
    );
}
